import { uniq } from 'lodash';
import moment from 'moment/moment';
import * as React from 'react';

import { AbsenceEntry } from '~/data/absences/types';
import { useEmployeeProfile } from '~/data/employeeProfile';
import { resolveAbsenceDtoArraysFromAbsenceEntries } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesCalendar/data';
import { People } from '~/services/Http';
import {
  AbsenceDto,
  EmployeeProfileDto,
} from '~/services/Http/client.people.generated';
import { useGetPublicHolidays } from '~/services/Http/hooks';

type PropsType = {
  employeeId: number;
  absenceEntries: AbsenceEntry[];
};

type UseAbsencesCalendarDataOutput = {
  teamAbsences: People.AbsenceDto[];
  publicHolidays?: People.PublicHolidayDto[];
  targetEmployee?: EmployeeProfileDto;
};

export const useAbsencesCalendarData = (
  props: PropsType,
): UseAbsencesCalendarDataOutput => {
  const { employeeId, absenceEntries } = props;

  const { profile } = useEmployeeProfile();

  const limitedAbsences = React.useMemo(() => {
    const { shortAbsences, longAbsences } =
      resolveAbsenceDtoArraysFromAbsenceEntries(
        employeeId,
        absenceEntries,
        profile,
      );

    return [...shortAbsences, ...longAbsences];
  }, [absenceEntries, profile, employeeId]);

  const legalEntities = React.useMemo(() => {
    if (profile?.legalEntity) {
      return [profile.legalEntity.id];
    }
    return [];
  }, [profile]);

  const getPublicHolidaysReqParams = React.useMemo(
    () => ({
      legalEntities,
      countryCodes: undefined,
      years: uniq([
        moment().toDate().getFullYear(),
        moment().add(30, 'day').toDate().getFullYear(),
      ]),
    }),
    [legalEntities],
  );
  const { data: publicHolidays } = useGetPublicHolidays(
    getPublicHolidaysReqParams,
  );

  return {
    teamAbsences: limitedAbsences as AbsenceDto[],
    publicHolidays,
    targetEmployee: profile,
  };
};
